import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './PublicPhotoGrid.css';

const API_BASE = process.env.REACT_APP_PUBLIC_API_URL || 'http://localhost:3002/api/public';

// Helper function to normalize album name from URL (convert hyphens to spaces)
const normalizeAlbumName = (albumName) => {
    return albumName ? albumName.replace(/-/g, ' ') : null;
};

function PublicPhotoGrid({ tenantName, albumName, tenant, onPhotoClick }) {
    const navigate = useNavigate();
    const [photos, setPhotos] = useState([]);
    const [albums, setAlbums] = useState([]);
    const [selectedAlbum, setSelectedAlbum] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [albumInitialized, setAlbumInitialized] = useState(false);

    // Track the last fetched album ID to prevent duplicate fetches
    const lastFetchedAlbumId = useRef(null);

    // Define fetch functions first (before useEffects that use them)
    const fetchAlbums = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE}/${tenantName}/albums`);
            setAlbums(response.data);
            setError('');
        } catch (error) {
            console.error('Error fetching albums:', error);
            setError('Failed to load albums');
        }
    }, [tenantName]);

    const fetchPhotos = useCallback(async (albumId) => {
        setLoading(true);
        try {
            const url = albumId
                ? `${API_BASE}/${tenantName}/photos?albumId=${albumId}`
                : `${API_BASE}/${tenantName}/photos`;

            const response = await axios.get(url);
            setPhotos(response.data);
            setError('');
        } catch (error) {
            console.error('Error fetching photos:', error);
            setError('Failed to load photos');
        } finally {
            setLoading(false);
        }
    }, [tenantName]);

    const handlePhotoClick = useCallback(async (photo) => {
        // Record the view
        try {
            await axios.post(`${API_BASE}/${tenantName}/photos/${photo.id}/view`);
        } catch (error) {
            console.error('Error recording view:', error);
        }
        // Call the parent's onPhotoClick handler with photo and photos array
        onPhotoClick(photo, photos);
    }, [tenantName, onPhotoClick, photos]);

    // Fetch albums when tenant changes
    useEffect(() => {
        fetchAlbums();
        setAlbumInitialized(false);
        lastFetchedAlbumId.current = null;
    }, [fetchAlbums]);

    // Set selected album from URL when albums are loaded
    useEffect(() => {
        if (albums.length > 0) {
            if (albumName) {
                const normalizedAlbumName = normalizeAlbumName(albumName);
                const album = albums.find(a => a.name.toLowerCase() === normalizedAlbumName.toLowerCase());
                if (album) {
                    // Only update if album ID changed
                    if (!selectedAlbum || selectedAlbum.id !== album.id) {
                        setSelectedAlbum(album);
                    }
                } else {
                    // Album not found, redirect to all photos
                    navigate(`/${tenantName}`);
                    setSelectedAlbum(null);
                }
            } else {
                setSelectedAlbum(null);
            }
            setAlbumInitialized(true);
        }
    }, [albums, albumName, tenantName, navigate, selectedAlbum]);

    // Fetch photos after album has been initialized from URL
    // Use album ID for comparison to prevent unnecessary fetches
    const selectedAlbumId = selectedAlbum?.id || null;

    useEffect(() => {
        if (albumInitialized && lastFetchedAlbumId.current !== selectedAlbumId) {
            lastFetchedAlbumId.current = selectedAlbumId;
            fetchPhotos(selectedAlbumId);
        }
    }, [selectedAlbumId, albumInitialized, fetchPhotos]);

    const handleBackToAlbums = useCallback(() => {
        navigate(`/${tenantName}`);
    }, [navigate, tenantName]);

    return (
        <div className="public-gallery-container">
            <header className="gallery-header">
                <button className="back-to-albums" onClick={handleBackToAlbums} title="Back to Albums">
                    &larr;
                </button>
                <h1>{tenant?.public_title || `${tenant?.name} Gallery`}</h1>
                {selectedAlbum && (
                    <p className="current-album-name">{selectedAlbum.name}</p>
                )}
            </header>


            {error && <div className="error">{error}</div>}

            {loading ? (
                <div className="loading-photos">
                    <p>Loading photos...</p>
                </div>
            ) : photos.length === 0 ? (
                <div className="no-photos">
                    <p>No published photos in this gallery yet.</p>
                </div>
            ) : (
                <div className="photo-grid">
                    {photos.map(photo => (
                        <div
                            key={photo.id}
                            className="photo-card"
                            onClick={() => handlePhotoClick(photo)}
                        >
                            <img
                                src={`${API_BASE}/${tenantName}/photos/${photo.id}/thumbnail`}
                                alt={photo.title || ''}
                            />
                            {(photo.title || photo.album_name) && (
                            <div className="photo-info">
                                {photo.title && <p className="photo-name">{photo.title}</p>}
                                {photo.album_name && <p className="photo-album">{photo.album_name}</p>}
                            </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default PublicPhotoGrid;
